import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"

import { api } from "@/lib/api"

// ───────────────────────────────────────────────────────────────────────
// Backend contracts (matched to legacy admin's POSTs):
//
//   POST /admin/album_list   { page, limit, search?, sortyBy?, sortDirection? }
//                           → { success, data: { docs, totalDocs, page, limit, totalPages, ... }, message }
//
//   POST /admin/album_get    { id }
//                           → { success, data: <album>, message }
//
//   POST /admin/album_create { category_id, category_ids, title, description,
//                              author, album_type, S3_cover_image,
//                              cover_image_name, is_carousel,
//                              is_notification, notification_message,
//                              order, carousel_order }
//                           → { success, data, message }
//
//   POST /admin/album_update { id, ...same as create }
//                           → { success, data, message }
//
//   POST /admin/album_delete { id }
//                           → { success, message }
//
// NOTE 1: backend expects the 'sortyBy' typo (extra "y") on the list. Preserved.
// NOTE 2: payload uses 'S3_cover_image' (capital S). Preserved exactly.
// NOTE 3: album_type is sent as a number (1=Free, 2=Paid). The list endpoint
//         returns it as a string in some records ("1"/"2") — render handles
//         both.
// ───────────────────────────────────────────────────────────────────────

export type AdminAlbum = {
  _id: string
  title?: string
  description?: string
  author?: string
  album_type?: number | string
  category_id?: { _id: string; name: string } | string | null
  category_ids?: string[]
  category_name?: string
  cover_image?: string
  cover_image_name?: string
  S3_cover_image?: unknown
  is_carousel?: boolean
  is_notification?: boolean
  notification_message?: string
  like_count?: number
  order?: number
  carousel_order?: number
  /** V-Q5 metadata fields surfaced on ContentItem.metadata. */
  producer?: string
  speaker?: string
  curator?: string
  publicly_available?: boolean
  /** V-Q10: admin soft-hide toggle. True = filtered out of every
   *  user-facing surface; still visible in admin lists. */
  is_hidden?: boolean
  created_at?: string
  updated_at?: string
}

export type AlbumListParams = {
  page: number
  limit: number
  search?: string
  /** Backend typo preserved: extra "y". */
  sortyBy?: string
  sortDirection?: 1 | -1
}

export type AlbumListResponse = {
  docs: AdminAlbum[]
  totalDocs: number
  page: number
  limit: number
  totalPages: number
}

const albumsListKey = (p: AlbumListParams) =>
  ["admin", "albums", "list", p] as const

export function useAlbumsList(params: AlbumListParams) {
  return useQuery({
    queryKey: albumsListKey(params),
    queryFn: async (): Promise<AlbumListResponse> => {
      const res = await api.post<{
        success: boolean
        data: AlbumListResponse
        message: string
      }>("/admin/album_list", params)
      const data = res.data?.data
      return {
        docs: Array.isArray(data?.docs) ? data.docs : [],
        totalDocs: typeof data?.totalDocs === "number" ? data.totalDocs : 0,
        page: typeof data?.page === "number" ? data.page : params.page,
        limit: typeof data?.limit === "number" ? data.limit : params.limit,
        totalPages: typeof data?.totalPages === "number" ? data.totalPages : 0,
      }
    },
    placeholderData: (prev) => prev,
  })
}

export function useGetAlbum(id: string | undefined) {
  return useQuery({
    queryKey: ["admin", "albums", "get", id] as const,
    queryFn: async (): Promise<AdminAlbum | null> => {
      if (!id) return null
      const res = await api.post<{
        success: boolean
        data: AdminAlbum
        message: string
      }>("/admin/album_get", { id })
      return res.data?.data ?? null
    },
    enabled: !!id,
  })
}

type AlbumWritePayload = {
  /** Present for update; omitted for create. */
  id?: string
  category_id?: string
  category_ids: string[]
  title: string
  description: string
  author: string
  album_type: number
  S3_cover_image?: unknown
  cover_image_name?: string
  is_carousel: boolean
  /** Only meaningful on create — legacy hides this on edit. */
  is_notification?: boolean
  notification_message?: string
  order: number
  carousel_order: number
  /** V-Q5 metadata. Empty string clears; absent leaves the existing value. */
  producer?: string
  speaker?: string
  curator?: string
  publicly_available?: boolean
  /** V-Q10 admin soft-hide toggle. */
  is_hidden?: boolean
}

export function useCreateAlbum() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (payload: AlbumWritePayload) => {
      const res = await api.post<{
        success: boolean
        data?: AdminAlbum
        message: string
      }>("/admin/album_create", payload)
      return res.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "albums"] })
    },
  })
}

export function useUpdateAlbum() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (payload: AlbumWritePayload) => {
      const res = await api.post<{
        success: boolean
        data?: AdminAlbum
        message: string
      }>("/admin/album_update", payload)
      return res.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "albums"] })
    },
  })
}

export function useDeleteAlbum() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await api.post("/admin/album_delete", { id })
      return res.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "albums"] })
    },
  })
}

// ───────────────────────────────────────────────────────────────────────
// Dropdown source. Used by ChapterForm to populate the album multi-select.
// Legacy fetched limit:2500 — we mirror so we don't lose albums in the UI.
// Heavily cached because album metadata changes rarely relative to chapter
// edits.
// ───────────────────────────────────────────────────────────────────────
type AlbumOption = { _id: string; title: string }

const ALBUM_DROPDOWN_LIMIT = 2500

export function useAllAlbums() {
  return useQuery({
    queryKey: ["admin", "albums", "all"] as const,
    queryFn: async (): Promise<AlbumOption[]> => {
      const res = await api.post<{
        success: boolean
        data: { docs?: { _id: string; title?: string }[] }
        message: string
      }>("/admin/album_list", {
        limit: ALBUM_DROPDOWN_LIMIT,
        page: 1,
        search: "",
      })
      const docs = res.data?.data?.docs
      if (!Array.isArray(docs)) return []
      return docs.map((d) => ({ _id: d._id, title: d.title ?? "(untitled)" }))
    },
    staleTime: 5 * 60_000,
  })
}
